import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  ConfigProvider,
  Flex,
  InputNumber,
  message,
  Radio,
  Table,
  Tooltip,
  Checkbox
} from 'antd';
import { Button } from '../../design-system';
import { useTheme } from '../../providers/theme';
import { getColors } from '../utils/getColors';
import ChartResults from '../chart/chartResults';
import { LazyMount } from '../chart/LazyMount';
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  ExpandOutlined,
  CompressOutlined
} from '@ant-design/icons';
import WaterOilTable from './waterOilTable/waterOilTable';
import { useSelected } from '../../providers/selectedContext';
import {
  createSeries,
  getChartData,
  getTableData,
  parseFUTableColumns,
  parseFUTableData
} from './utils';
import { ResultData, TableData } from './types';
import GasOilTable from './gasOilTable/gasOilTable';
import { useAppContext } from '../../providers/AppContext';
import EditFlowUnitModal from './modal/editFlowUnitModal';
import { AnalyzeType } from '../../@types/samples/types';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { Project, Zone } from '../../@types/projects/types';
import { useCorrelation } from '../../providers/Correlation';
import {
  DEFAULT_RESULTS_CURVE_POINTS,
  MAX_CURVE_POINTS,
  MIN_CURVE_POINTS,
  clampCurvePointCount
} from '../utils/curveGeneration';
import { SystemSaturationCalculator } from './worker/calculator';
import { projectNeedsKrPcRecalculation } from './recalculation';

const height = ['46%', '50%', '54%', '60%', '70%', '80%', '90%', '100%'];
const width = ['32%', '49%', '100%'];

type TChartSeries = {
  pcowXsw: ResultData[];
  heightXsw: ResultData[];
  rpXsw: ResultData[];
  rpXsg: ResultData[];
};

type WorkerSuccessResponse = {
  status: 'success';
  data: Project;
  requestId?: number;
};

type WorkerErrorResponse = {
  status: 'error';
  message?: string;
  requestId?: number;
};

const EMPTY_SERIES: TChartSeries = {
  pcowXsw: [],
  heightXsw: [],
  rpXsw: [],
  rpXsg: []
};

const EMPTY_TABLE_DATA: TableData = {
  fzi: {},
  fziMin: {},
  fziMax: {},
  a: {},
  b: {},
  lambda: {},
  pce: {},
  pcmax_ow: {},
  swir: {},
  no_w: {},
  nw: {},
  krw: {},
  sorw: {},
  kro: {},
  no_g: {},
  ng: {},
  krg: {},
  sgc: {},
  sor: {},
  sgmax: {},
  sorg: {},
  pcmax_og: {}
};

const getZonesForXAxis = (
  rawZones: Zone[] | null | undefined,
  selectedXAxis: 'fzi' | 'rqi'
): Zone[] =>
  rawZones?.map(zone => ({
    ...zone,
    zone_values:
      zone.zone_values_by_x_axis?.[selectedXAxis] ??
      (selectedXAxis === 'fzi' ? zone.zone_values : null),
    zone_curves:
      zone.zone_curves_by_x_axis?.[selectedXAxis] ??
      (selectedXAxis === 'fzi' ? zone.zone_curves : null)
  })) ?? [];

interface ResultsProps {
  /** Optional immutable project snapshot, used by the Applied Auto tab. */
  projectOverride?: Project;
  xAxisOverride?: 'fzi' | 'rqi';
}

const Results = ({ projectOverride, xAxisOverride }: ResultsProps = {}) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);

  const { selectedInfo, setSelectedInfo } = useSelected();
  const { configStates, updateConfigStates } = useAppContext();
  const { selectedXAxis } = useCorrelation();
  const activeXAxis = xAxisOverride ?? selectedXAxis;
  const [calculatedOverride, setCalculatedOverride] = useState<Project | null>(
    projectOverride ?? null
  );

  useEffect(() => {
    setCalculatedOverride(projectOverride ?? null);
  }, [projectOverride]);

  const project = projectOverride ? calculatedOverride : selectedInfo.project;
  const rawZones = project?.correlation?.zones;
  const zones = useMemo(
    () => getZonesForXAxis(rawZones, activeXAxis),
    [rawZones, activeXAxis]
  );

  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [showMultipleResults, setShowMultipleResults] = useState(false);
  const [showScaledKr, setShowScaledKr] = useState(false);
  const [numberOfPoints, setNumberOfPoints] = useState<number>(
    DEFAULT_RESULTS_CURVE_POINTS
  );
  const [selectedAnalysisType, setSelectedAnalysisType] = useState<AnalyzeType>(
    project?.type_of_analyze ?? AnalyzeType.LIAO
  );

  const [chartHeight, setChartHeight] = useState(height[3]);
  const [containerWidth, setContainerWidth] = useState(width[1]);
  const [tableColumns, setTableColumns] = useState(parseFUTableColumns());
  const [tableData, setTableData] = useState<TableData>(EMPTY_TABLE_DATA);

  // Pre-computed chart series for both types — never updated on type switch
  const [liaoSeries, setLiaoSeries] = useState<TChartSeries>(EMPTY_SERIES);
  const [lambdaSeries, setLambdaSeries] = useState<TChartSeries>(EMPTY_SERIES);
  // Combined series used only when "show both" mode is active
  const [combinedSeries, setCombinedSeries] =
    useState<TChartSeries>(EMPTY_SERIES);

  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef(0);
  const latestAppliedRequestIdRef = useRef(0);
  const isCalculatingRef = useRef(false);

  useEffect(() => {
    workerRef.current = new Worker(
      new URL('./worker/calculation.worker.ts', import.meta.url),
      {
        type: 'module'
      }
    );
    return () => {
      workerRef.current?.terminate();
    };
  }, []);

  const buildChartSeries = useCallback(
    (zones: Zone[], type: AnalyzeType): TChartSeries => {
      const chartData = getChartData(zones!, type);
      return {
        pcowXsw: chartData.map((data, i) =>
          createSeries(`RT${i + 1}`, data.pcowXsw, data.color)
        ),
        heightXsw: chartData.map((data, i) =>
          createSeries(`RT${i + 1}`, data.heightXsw, data.color)
        ),
        rpXsw: chartData.flatMap((data, i) => [
          createSeries(`RT${i + 1} Krw`, data.krwXsw, data.color),
          createSeries(`RT${i + 1} Krow`, data.krowXsw, data.color)
        ]),
        rpXsg: chartData.flatMap((data, i) => [
          createSeries(`RT${i + 1} Krg`, data.krgXsg, data.color),
          createSeries(`RT${i + 1} Krog`, data.krogXsg, data.color)
        ])
      };
    },
    []
  );

  const runCalculationInWorker = useCallback(
    (
      projectToCalculate: Project,
      n: number
    ): Promise<{ project: Project; requestId: number }> => {
      return new Promise((resolve, reject) => {
        const worker = workerRef.current;
        if (!worker)
          return reject(new Error('Calculation worker is not initialized.'));
        const requestId = ++requestIdRef.current;

        const handleMessage = (
          event: MessageEvent<WorkerSuccessResponse | WorkerErrorResponse>
        ) => {
          const response = event.data;
          if (response.requestId !== requestId) return;

          cleanup();
          if (response.status === 'success') {
            resolve({ project: response.data, requestId });
            return;
          }

          reject(
            new Error(
              response.message ?? 'An error occurred while recalculating.'
            )
          );
        };
        const handleError = (error: ErrorEvent) => {
          cleanup();
          reject(error);
        };
        const cleanup = () => {
          worker.removeEventListener('message', handleMessage);
          worker.removeEventListener('error', handleError);
        };

        worker.addEventListener('message', handleMessage);
        worker.addEventListener('error', handleError);
        worker.postMessage({
          project: projectToCalculate,
          numberOfPoints: n,
          requestId
        });
      });
    },
    []
  );

  const handleExpand = () => {
    const i = height.indexOf(chartHeight);
    if (i < height.length - 1) setChartHeight(height[i + 1]);
  };
  const handleCompress = () => {
    const i = height.indexOf(chartHeight);
    if (i > 0) setChartHeight(height[i - 1]);
  };
  const handleZoomIn = () => {
    const i = width.indexOf(containerWidth);
    if (i < width.length - 1) setContainerWidth(width[i + 1]);
  };
  const handleZoomOut = () => {
    const i = width.indexOf(containerWidth);
    if (i > 0) setContainerWidth(width[i - 1]);
  };

  const handleShowScaledKr = useCallback((event: CheckboxChangeEvent) => {
    setShowScaledKr(event.target.checked);
    localStorage.setItem('showScaledKr', String(event.target.checked));
  }, []);

  useEffect(() => {
    const savedShowScaledKr = localStorage.getItem('showScaledKr') === 'true';
    setShowScaledKr(savedShowScaledKr);
  }, []);

  // Reset per-project state when project changes
  useEffect(() => {
    if (!project?.uid) return;
    setSelectedAnalysisType(project.type_of_analyze ?? AnalyzeType.LIAO);
    setShowMultipleResults(false);
    const rawN = localStorage.getItem(`resultsNumberOfPoints_${project.uid}`);
    if (rawN !== null) {
      const savedN = parseInt(rawN, 10);
      if (
        !isNaN(savedN) &&
        savedN >= MIN_CURVE_POINTS &&
        savedN <= MAX_CURVE_POINTS
      ) {
        setNumberOfPoints(clampCurvePointCount(savedN));
        return;
      }
    }
    setNumberOfPoints(DEFAULT_RESULTS_CURVE_POINTS);
  }, [project?.uid]);

  // Pre-compute both curve series and the table for the selected methodology.
  useEffect(() => {
    if (!zones?.length) return;
    const td = getTableData(zones, selectedAnalysisType);
    const columns = parseFUTableColumns(zones);
    setLiaoSeries(buildChartSeries(zones, AnalyzeType.LIAO));
    setLambdaSeries(buildChartSeries(zones, AnalyzeType.LAMBDA));
    setTableData(td);
    setTableColumns(columns);
  }, [zones, selectedAnalysisType, buildChartSeries]);

  const handleNumberOfPointsChange = useCallback(
    (val: number | null) => {
      if (val == null || val < 10) return;
      const nextValue = clampCurvePointCount(val);
      setNumberOfPoints(nextValue);
      if (project?.uid) {
        localStorage.setItem(
          `resultsNumberOfPoints_${project.uid}`,
          String(nextValue)
        );
      }
    },
    [project?.uid]
  );

  useEffect(() => {
    if (!project || !zones.length) return;
    if (
      !projectNeedsKrPcRecalculation(
        zones,
        numberOfPoints,
        project.correlation?.grad_diff
      )
    ) {
      setLoadingAnalysis(false);
      return;
    }
    if (isCalculatingRef.current) return;

    const applyResult = (updatedProject: Project, requestId?: number) => {
      if (
        requestId !== undefined &&
        requestId < latestAppliedRequestIdRef.current
      )
        return;
      if (requestId !== undefined)
        latestAppliedRequestIdRef.current = requestId;

      const nextZones = getZonesForXAxis(
        updatedProject.correlation?.zones,
        activeXAxis
      );
      if (projectOverride) {
        setCalculatedOverride(updatedProject);
      } else if (
        JSON.stringify(nextZones) !== JSON.stringify(zones)
      ) {
        setSelectedInfo({ project: updatedProject });
      }
    };

    isCalculatingRef.current = true;
    setLoadingAnalysis(true);
    runCalculationInWorker(project, numberOfPoints)
      .then(({ project: updatedProject, requestId }) => {
        applyResult(updatedProject, requestId);
      })
      .catch(err => {
        // Worker failed (e.g. module load error on first visit). Fall back to
        // synchronous calculation on the main thread so charts still render.
        console.warn(
          'Worker calculation failed, falling back to sync:',
          err.message || err
        );
        try {
          const calculator = new SystemSaturationCalculator();
          const updatedProject = calculator.calculateBoth(
            { ...project },
            numberOfPoints
          );
          applyResult(updatedProject);
        } catch (syncErr: any) {
          console.error('Sync fallback also failed:', syncErr);
          message.error(
            syncErr.message || 'An error occurred while recalculating.'
          );
        }
      })
      .finally(() => {
        setLoadingAnalysis(false);
        isCalculatingRef.current = false;
      });
  }, [
    numberOfPoints,
    project,
    runCalculationInWorker,
    activeXAxis,
    projectOverride,
    setSelectedInfo,
    zones
  ]);

  // Combined series for "show both" overlay — computed on demand
  const handleShowMultiple = () => {
    if (!zones?.length) return;
    const liaoData = getChartData(zones, AnalyzeType.LIAO);
    const lambdaData = getChartData(zones, AnalyzeType.LAMBDA);

    setCombinedSeries({
      pcowXsw: [
        ...liaoData.map((d, i) =>
          createSeries(`RT${i + 1} Liao`, d.pcowXsw, '#f47936')
        ),
        ...lambdaData.map((d, i) =>
          createSeries(`RT${i + 1} Brooks-Corey`, d.pcowXsw, '#72E2D8')
        )
      ],
      heightXsw: [
        ...liaoData.map((d, i) =>
          createSeries(`RT${i + 1} Liao`, d.heightXsw, '#f47936')
        ),
        ...lambdaData.map((d, i) =>
          createSeries(`RT${i + 1} Brooks-Corey`, d.heightXsw, '#72E2D8')
        )
      ],
      rpXsw: [
        ...liaoData.flatMap((d, i) => [
          createSeries(`RT${i + 1} Krw Liao`, d.krwXsw, '#f47936'),
          createSeries(`RT${i + 1} Krow Liao`, d.krowXsw, '#f47936')
        ]),
        ...lambdaData.flatMap((d, i) => [
          createSeries(`RT${i + 1} Krw Brooks-Corey`, d.krwXsw, '#72E2D8'),
          createSeries(`RT${i + 1} Krow Brooks-Corey`, d.krowXsw, '#72E2D8')
        ])
      ],
      rpXsg: [
        ...liaoData.flatMap((d, i) => [
          createSeries(`RT${i + 1} Krg Liao`, d.krgXsw, '#f47936'),
          createSeries(`RT${i + 1} Krog Liao`, d.krogXsw, '#f47936')
        ]),
        ...lambdaData.flatMap((d, i) => [
          createSeries(`RT${i + 1} Krg Brooks-Corey`, d.krgXsw, '#72E2D8'),
          createSeries(`RT${i + 1} Krog Brooks-Corey`, d.krogXsw, '#72E2D8')
        ])
      ]
    });
    setShowMultipleResults(true);
  };

  const handleResultsClick = () => {
    if (!showMultipleResults) {
      handleShowMultiple();
    } else {
      setShowMultipleResults(false);
    }
  };

  // Scaled Kr series — pre-computed for both types
  const scaledRpXsw = useMemo(() => {
    if (!zones?.length) return { liao: [], lambda: [] };
    const liaoData = getChartData(zones, AnalyzeType.LIAO);
    const lambdaData = getChartData(zones, AnalyzeType.LAMBDA);
    const build = (chartData: ReturnType<typeof getChartData>) =>
      chartData.flatMap((data, i) => {
        if (!data.krwscaledXsw || !data.krowscaledXsw) return [];
        return [
          createSeries(
            `RT${i + 1} Krw (scaled)`,
            data.krwscaledXsw,
            data.color
          ),
          createSeries(
            `RT${i + 1} Krow (scaled)`,
            data.krowscaledXsw,
            data.color
          )
        ];
      });
    return { liao: build(liaoData), lambda: build(lambdaData) };
  }, [zones]);

  const scaledRpXsg = useMemo(() => {
    if (!zones?.length) return { liao: [], lambda: [] };
    const liaoData = getChartData(zones, AnalyzeType.LIAO);
    const lambdaData = getChartData(zones, AnalyzeType.LAMBDA);
    const build = (chartData: ReturnType<typeof getChartData>) =>
      chartData.flatMap((data, i) => {
        if (!data.krgscaledXsg || !data.krogscaledXsg) return [];
        return [
          createSeries(
            `RT${i + 1} Krg (scaled)`,
            data.krgscaledXsg,
            data.color
          ),
          createSeries(
            `RT${i + 1} Krog (scaled)`,
            data.krogscaledXsg,
            data.color
          )
        ];
      });
    return { liao: build(liaoData), lambda: build(lambdaData) };
  }, [zones]);

  const removeLastPoint = useCallback(
    (seriesArr: ResultData[]) =>
      seriesArr.map(s => ({
        ...s,
        data:
          Array.isArray(s.data) && s.data.length > 1
            ? s.data.slice(0, -1)
            : s.data
      })),
    []
  );

  // Derive display data for each type — no state update, instant
  const liaoRpXswDisplay = useMemo(
    () =>
      showScaledKr
        ? removeLastPoint(scaledRpXsw.liao)
        : removeLastPoint(liaoSeries.rpXsw),
    [showScaledKr, scaledRpXsw.liao, liaoSeries.rpXsw, removeLastPoint]
  );
  const lambdaRpXswDisplay = useMemo(
    () =>
      showScaledKr
        ? removeLastPoint(scaledRpXsw.lambda)
        : removeLastPoint(lambdaSeries.rpXsw),
    [showScaledKr, scaledRpXsw.lambda, lambdaSeries.rpXsw, removeLastPoint]
  );
  const liaoRpXsgDisplay = useMemo(
    () => (showScaledKr ? scaledRpXsg.liao : liaoSeries.rpXsg),
    [showScaledKr, scaledRpXsg.liao, liaoSeries.rpXsg]
  );
  const lambdaRpXsgDisplay = useMemo(
    () => (showScaledKr ? scaledRpXsg.lambda : lambdaSeries.rpXsg),
    [showScaledKr, scaledRpXsg.lambda, lambdaSeries.rpXsg]
  );

  const getMultipleLabel = () => {
    if (showMultipleResults) {
      return selectedAnalysisType === AnalyzeType.LIAO
        ? 'Hide Brooks-Corey results'
        : 'Hide Liao results';
    }
    return selectedAnalysisType === AnalyzeType.LIAO
      ? 'Show Brooks-Corey results'
      : 'Show Liao results';
  };

  const renderChartBlock = (
    series: TChartSeries,
    rpXswData: ResultData[],
    rpXsgData: ResultData[]
  ) => (
    <Flex wrap="wrap" gap="small" justify="space-between">
      <LazyMount style={{ width: containerWidth, minHeight: 360 }}>
        <ChartResults
          containerWidth="100%"
          height={chartHeight}
          title="Oil-Water System"
          yAxis="Pcow (Psi)"
          xAxis="Water Saturation, Sw"
          data={series.pcowXsw}
        />
      </LazyMount>
      <LazyMount style={{ width: containerWidth, minHeight: 360 }}>
        <ChartResults
          containerWidth="100%"
          height={chartHeight}
          title="Oil-Water System"
          yAxis="Height (m)"
          xAxis="Water Saturation, Sw"
          data={series.heightXsw}
        />
      </LazyMount>
      <LazyMount style={{ width: containerWidth, minHeight: 360 }}>
        <ChartResults
          containerWidth="100%"
          height={chartHeight}
          title="Oil-Water System"
          yAxis="Relative Permeability"
          xAxis="Water Saturation, Sw"
          data={rpXswData}
        />
      </LazyMount>
      <LazyMount style={{ width: containerWidth, minHeight: 360 }}>
        <ChartResults
          containerWidth="100%"
          height={chartHeight}
          title="Gas-Oil System"
          yAxis="Relative Permeability"
          xAxis="Gas Saturation, Sg"
          data={rpXsgData}
        />
      </LazyMount>
    </Flex>
  );

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: colors.colorPrimary2,
          colorText: colors.text,
          fontSize: 16,
          colorBgContainer: 'transparent',
          colorBorder: colors.colorPrimary2
        },
        components: {
          Checkbox: { colorTextDisabled: 'gray', colorText: colors.text }
        }
      }}
    >
      {selectedInfo.project?.correlation?.zones?.length ? (
        <div style={{ padding: '1rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '0.5rem'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}
            >
              <Tooltip title="Zoom in charts">
                <Button
                  variant="ghost"
                  size="sm"
                  startIcon={<ZoomInOutlined />}
                  onClick={handleZoomIn}
                />
              </Tooltip>
              <Tooltip title="Zoom out charts">
                <Button
                  variant="ghost"
                  size="sm"
                  startIcon={<ZoomOutOutlined />}
                  onClick={handleZoomOut}
                />
              </Tooltip>
              <Checkbox checked={showScaledKr} onChange={handleShowScaledKr}>
                Scaled Kr
              </Checkbox>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <span style={{ fontSize: '13px', color: colors.text }}>
                  Curve points:
                </span>
                <InputNumber
                  min={MIN_CURVE_POINTS}
                  max={MAX_CURVE_POINTS}
                  step={10}
                  value={numberOfPoints}
                  onChange={handleNumberOfPointsChange}
                  size="small"
                  style={{ width: 70 }}
                />
              </div>
            </div>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Button variant="secondary" onClick={handleResultsClick}>
                {getMultipleLabel()}
              </Button>
              <Tooltip title="Expand charts">
                <Button
                  variant="ghost"
                  size="sm"
                  startIcon={<ExpandOutlined />}
                  onClick={handleExpand}
                />
              </Tooltip>
              <Tooltip title="Compress charts">
                <Button
                  variant="ghost"
                  size="sm"
                  startIcon={<CompressOutlined />}
                  onClick={handleCompress}
                />
              </Tooltip>
            </div>
          </div>

          <Radio.Group
            value={selectedAnalysisType}
            onChange={e => {
              setSelectedAnalysisType(e.target.value);
              setShowMultipleResults(false);
            }}
            options={[
              { label: 'Liao Analysis', value: AnalyzeType.LIAO },
              { label: 'Brooks-Corey Analysis', value: AnalyzeType.LAMBDA }
            ]}
            style={{
              width: '100%',
              backgroundColor: 'transparent !important',
              paddingTop: '1rem'
            }}
            block
            optionType="button"
            buttonStyle="solid"
            className={isDay ? 'day-radio' : 'night-radio'}
          />

          {/* "Show both" overlay — single chart block with combined series */}
          {showMultipleResults && (
            <Flex wrap="wrap" gap="small" justify="space-between">
              <LazyMount style={{ width: containerWidth, minHeight: 360 }}>
                <ChartResults
                  containerWidth="100%"
                  height={chartHeight}
                  title="Oil-Water System"
                  yAxis="Pcow (Psi)"
                  xAxis="Water Saturation, Sw"
                  data={combinedSeries.pcowXsw}
                />
              </LazyMount>
              <LazyMount style={{ width: containerWidth, minHeight: 360 }}>
                <ChartResults
                  containerWidth="100%"
                  height={chartHeight}
                  title="Oil-Water System"
                  yAxis="Height (m)"
                  xAxis="Water Saturation, Sw"
                  data={combinedSeries.heightXsw}
                />
              </LazyMount>
              <LazyMount style={{ width: containerWidth, minHeight: 360 }}>
                <ChartResults
                  containerWidth="100%"
                  height={chartHeight}
                  title="Oil-Water System"
                  yAxis="Relative Permeability"
                  xAxis="Water Saturation, Sw"
                  data={combinedSeries.rpXsw}
                />
              </LazyMount>
              <LazyMount style={{ width: containerWidth, minHeight: 360 }}>
                <ChartResults
                  containerWidth="100%"
                  height={chartHeight}
                  title="Gas-Oil System"
                  yAxis="Relative Permeability"
                  xAxis="Gas Saturation, Sg"
                  data={combinedSeries.rpXsg}
                />
              </LazyMount>
            </Flex>
          )}

          {loadingAnalysis &&
            projectNeedsKrPcRecalculation(
              zones,
              numberOfPoints,
              project?.correlation?.grad_diff
            ) && (
              <div style={{ paddingTop: '1rem', color: colors.text }}>
                Recalculating Kr x Pc curves...
              </div>
            )}

          {!showMultipleResults &&
            (selectedAnalysisType === AnalyzeType.LIAO
              ? renderChartBlock(liaoSeries, liaoRpXswDisplay, liaoRpXsgDisplay)
              : renderChartBlock(
                  lambdaSeries,
                  lambdaRpXswDisplay,
                  lambdaRpXsgDisplay
                ))}

          <div style={{ marginTop: '2rem' }}>
            <ConfigProvider
              theme={{
                components: {
                  Table: {
                    headerBg: isDay ? '#fafafa' : '#090909',
                    borderColor: isDay ? '#f0f0f0' : '#434343',
                    rowHoverBg: isDay ? '#f5f5f5' : '#1f1f1f',
                    stickyScrollBarBg: isDay ? 'rgba(0, 0, 0, 0.25)' : '#434343'
                  }
                },
                token: {
                  colorText: colors.text,
                  colorBorder: colors.border,
                  colorBgBase: colors.bg
                }
              }}
            >
              <Table
                columns={tableColumns}
                dataSource={parseFUTableData(
                  tableData,
                  activeXAxis.toUpperCase(),
                  selectedAnalysisType
                )}
                pagination={false}
                size="small"
                bordered
                expandable={{
                  defaultExpandAllRows: true,
                  rowExpandable: record => record.children !== undefined
                }}
              />
              <Button
                variant="secondary"
                style={{ marginTop: '1rem' }}
                onClick={() =>
                  updateConfigStates({
                    openEditFlowUnits: !configStates.openEditFlowUnits
                  })
                }
              >
                Edit values
              </Button>
              <EditFlowUnitModal />
              <WaterOilTable
                showScaledKr={showScaledKr}
                analysisType={selectedAnalysisType}
                zones={zones}
              />
              <GasOilTable
                showScaledKr={showScaledKr}
                analysisType={selectedAnalysisType}
                zones={zones}
              />
            </ConfigProvider>
          </div>
        </div>
      ) : (
        <div>
          To see the Results tab the project must have correlation and zones
        </div>
      )}
    </ConfigProvider>
  );
};

export default Results;
